import React, { useContext, useMemo } from 'react';
import {
  NavigationItem as SiteNavigationItem,
  SiteNavigation
} from '@sierralabs/site-ui';

import environment from '../environments';
import { UserContext, UserContextType } from '../user/User.context';
import { NavigationItem as EnvNavigationItem } from '../environments/navigation';
import { User } from '../user/User.interface';
import { useJiraConnectAPI } from './jira-connect/JiraConnect.hooks';

export type NavigationItem = EnvNavigationItem;

const navigationList: NavigationItem[] = environment.navigation;

export interface NavigationListProps {
  onNavigationItemClicked?: (item: NavigationItem) => void;
  renderAfterItem?: (item: NavigationItem) => JSX.Element | null;
}

export function hasAccess(item: NavigationItem, user: User) {
  const { roles: userRoles = [] } = user;
  const { roles, excludeRoles = [], allowNoRoles = false } = item;
  if (userRoles.length === 0 && allowNoRoles) {
    return true;
  }
  const condition = (role: string) => userRoles.find(r => r.name === role);
  // if a user has a role in excludeRoles, revoke access
  // if no roles list defined, skip condition check
  return !excludeRoles.some(condition) && (!roles || roles.some(condition));
}

function deepFilter(items: NavigationItem[], user: UserContextType) {
  return items
    .filter(item => item && user && hasAccess(item, user))
    .map(item => {
      if (item.items) {
        item = { ...item, items: deepFilter(item.items, user) };
      }
      return item;
    });
}

const NavigationList: React.FC<NavigationListProps> = props => {
  const { onNavigationItemClicked } = props;
  const userContext = useContext(UserContext);
  const { user } = userContext;
  const { isAvailable } = useJiraConnectAPI();

  const items = useMemo(() => {
    const items = deepFilter(navigationList, user);
    return items.map(item => {
      if (item.label === 'JIRA' && isAvailable) {
        item = { ...item, path: '/portal/jira-connect' };
      }
      return item as SiteNavigationItem;
    });
  }, [isAvailable, user]);

  return (
    <SiteNavigation
      navigation={items}
      component={{
        id: '',
        name: 'Site Navigation',
        type: 'site-navigation',
        depth: 0,
        position: 0,
        props: { navigation: items }
      }}
      onNavigationItemClicked={item => {
        onNavigationItemClicked?.(item as NavigationItem);
      }}
    />
  );
};

export default NavigationList;
